import { useRef, useState } from 'react';

/**
 * DocumentUploader — affiche un document existant avec options modifier/supprimer,
 * ou une zone d'upload si aucun document n'est présent.
 *
 * Props:
 *  label    {string}   Nom du document
 *  docUrl   {string}   URL Cloudinary du document (null si absent)
 *  onUpload {function} Callback onChange du file input (e) => ...
 *  onDelete {function} (optionnel) Callback pour supprimer le document
 */
function DocumentUploader({ label, docUrl, onUpload, onDelete }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(false);
  const isImage = docUrl && /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(docUrl);

  return (
    <div className="mb-3 border rounded-3 overflow-hidden">
      {/* En-tête */}
      <div className="d-flex align-items-center justify-content-between px-3 py-2 bg-white border-bottom">
        <div className="d-flex align-items-center gap-2">
          <i
            className={`bi ${docUrl ? 'bi-file-earmark-check-fill text-success' : 'bi-file-earmark-x text-muted'}`}
            style={{ fontSize: 18 }}
          ></i>
          <span className="fw-semibold" style={{ fontSize: 14 }}>
            {label}
          </span>
        </div>
        <span
          className={`badge ${docUrl ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}
          style={{ fontSize: 11 }}
        >
          {docUrl ? 'Uploadé' : 'Manquant'}
        </span>
      </div>

      {/* Corps */}
      <div className="px-3 py-3 bg-body-tertiary">
        {docUrl ? (
          <>
            {/* Aperçu condensé */}
            {preview && (
              <div className="mb-3">
                {isImage ? (
                  <img
                    src={docUrl}
                    alt={label}
                    className="rounded-3 w-100"
                    style={{ maxHeight: 220, objectFit: 'contain' }}
                  />
                ) : (
                  <iframe
                    src={docUrl}
                    width="100%"
                    height="260px"
                    className="rounded-3 border"
                    title={label}
                  />
                )}
              </div>
            )}

            {/* Actions */}
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPreview((v) => !v)}
              >
                <i className={`bi ${preview ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
                {preview ? 'Masquer' : 'Aperçu'}
              </button>
              <a
                href={docUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>Ouvrir
              </a>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                onClick={() => inputRef.current?.click()}
              >
                <i className="bi bi-pencil me-1"></i>Modifier
              </button>
              {onDelete && (
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={onDelete}>
                  <i className="bi bi-trash me-1"></i>Supprimer
                </button>
              )}
            </div>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-primary w-100"
            onClick={() => inputRef.current?.click()}
          >
            <i className="bi bi-upload me-2"></i>Uploader {label}
          </button>
        )}

        {/* Input caché */}
        <input
          ref={inputRef}
          type="file"
          className="d-none"
          accept="image/*,.pdf"
          onChange={onUpload}
        />
      </div>
    </div>
  );
}

export default DocumentUploader;
